// ABOUTME: Admin drawer to manage an operator's email notification recipients, scoped to the integrations the operator actually has.
import {
  useOperatorNotificationSettings,
  useAddNotificationRecipient,
  useUpdateNotificationRecipient,
  useRemoveNotificationRecipient,
  useNotificationHistory,
  usePollingItems,
} from '../../hooks/useAdminQueries';
import { ChangeHistoryEntries } from './ChangeHistoryList';
import {
  NotificationRecipient,
  NotificationEventSetting,
} from '../../api/adminApi';
import {
  EventDescriptor,
  INTEGRATION_DISABLED_EVENT,
  TRANSMORMER_EVENT,
  LEGACY_INTEGRATION_EVENTS,
} from '../../constants/notificationEvents';
import { useCurrentRole } from '../../hooks/useRole';
import { useMemo, useState } from 'react';
import './NotificationSettingsDrawer.css';

interface NotificationSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  operatorId: string;
  operatorName?: string;
}

const EDIT_ROLES = ['admin', 'super_admin'];
const DEFAULT_DELAY = 10;
const HUB = 'TECHLOG';

type DraftSettings = Record<string, NotificationEventSetting>;

// A titled group of events. An empty title renders the events with no heading
// (used for the operator-level Integration disabled toggle).
interface EventSection {
  title: string;
  events: EventDescriptor[];
}

function sectionKeys(sections: EventSection[]): string[] {
  return sections.flatMap((section) => section.events.map((e) => e.key));
}

// Seed a draft for exactly the visible events; events the operator hasn't
// stored yet start disabled, so a save persists a complete, predictable map.
function seedDraft(
  stored: Record<string, NotificationEventSetting>,
  keys: string[],
): DraftSettings {
  const draft: DraftSettings = {};
  for (const key of keys) {
    const current = stored?.[key];
    draft[key] = {
      enabled: current?.enabled ?? false,
      emailDelayMinutes: current?.emailDelayMinutes ?? DEFAULT_DELAY,
    };
  }
  return draft;
}

function allEnabledDraft(keys: string[]): DraftSettings {
  const draft: DraftSettings = {};
  for (const key of keys) {
    draft[key] = { enabled: true, emailDelayMinutes: DEFAULT_DELAY };
  }
  return draft;
}

interface RecipientCardProps {
  operatorId: string;
  recipient: NotificationRecipient;
  sections: EventSection[];
  canEdit: boolean;
}

function RecipientCard({
  operatorId,
  recipient,
  sections,
  canEdit,
}: RecipientCardProps) {
  const update = useUpdateNotificationRecipient();
  const remove = useRemoveNotificationRecipient();

  const keys = useMemo(() => sectionKeys(sections), [sections]);
  const [role, setRole] = useState<'client' | 'internal'>(
    recipient.recipientRole,
  );
  const [draft, setDraft] = useState<DraftSettings>(() =>
    seedDraft(recipient.notificationSettings, keys),
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const setEnabled = (key: string, enabled: boolean) =>
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], enabled } }));

  const setDelay = (key: string, minutes: number) =>
    setDraft((prev) => ({
      ...prev,
      [key]: { ...prev[key], emailDelayMinutes: minutes },
    }));

  const handleSave = async () => {
    setSaveError(null);
    try {
      await update.mutateAsync({
        operatorId,
        subId: recipient.id,
        data: { recipientRole: role, notificationSettings: draft },
      });
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save recipient',
      );
    }
  };

  const handleRemove = () =>
    remove.mutateAsync({ operatorId, subId: recipient.id });

  const renderEvent = (event: EventDescriptor, prefix: string) => {
    const label = prefix ? `${prefix} ${event.label}` : event.label;
    const enabled = draft[event.key]?.enabled ?? false;
    return (
      <div key={event.key} className="notif-event-row">
        <label className="notif-event-label">
          <input
            type="checkbox"
            aria-label={label}
            checked={enabled}
            disabled={!canEdit}
            onChange={(e) => setEnabled(event.key, e.target.checked)}
          />
          <span>{event.label}</span>
        </label>
        <span className="notif-event-delay">
          <input
            type="number"
            min={1}
            aria-label={`${label} delay minutes`}
            value={draft[event.key]?.emailDelayMinutes ?? DEFAULT_DELAY}
            disabled={!canEdit || !enabled}
            onChange={(e) =>
              setDelay(
                event.key,
                Math.max(1, Number(e.target.value) || DEFAULT_DELAY),
              )
            }
          />
          <span className="notif-event-delay-unit">min</span>
        </span>
      </div>
    );
  };

  return (
    <div
      className="notif-recipient"
      data-testid={`notif-recipient-${recipient.email}`}
    >
      <div className="notif-recipient-header">
        <span className="notif-recipient-email">{recipient.email}</span>
        <select
          className="notif-role-select"
          aria-label={`Role for ${recipient.email}`}
          value={role}
          disabled={!canEdit}
          onChange={(e) => setRole(e.target.value as 'client' | 'internal')}
        >
          <option value="client">Client</option>
          <option value="internal">Internal</option>
        </select>
        {canEdit && (
          <button
            className="notif-remove-btn"
            data-testid={`notif-remove-${recipient.email}`}
            onClick={handleRemove}
            disabled={remove.isPending}
          >
            Remove
          </button>
        )}
      </div>

      {sections.map((section) =>
        section.title ? (
          <div key={section.title} className="notif-event-group">
            <h4 className="notif-event-group-title">{section.title}</h4>
            {section.events.map((event) => renderEvent(event, section.title))}
          </div>
        ) : (
          <div key="__operator" className="notif-event-group notif-event-top">
            {section.events.map((event) => renderEvent(event, ''))}
          </div>
        ),
      )}

      {canEdit && (
        <div className="notif-recipient-footer">
          <button
            className="btn-save"
            data-testid={`notif-save-${recipient.email}`}
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving...' : 'Save'}
          </button>
          {saveError && <span className="drawer-error">{saveError}</span>}
        </div>
      )}
    </div>
  );
}

export function NotificationSettingsDrawer({
  isOpen,
  onClose,
  operatorId,
  operatorName,
}: NotificationSettingsDrawerProps) {
  const role = useCurrentRole();
  const canEdit = !!role && EDIT_ROLES.includes(role);

  const [activeTab, setActiveTab] = useState<'recipients' | 'history'>(
    'recipients',
  );

  const { data, isLoading } = useOperatorNotificationSettings(
    operatorId,
    isOpen,
  );
  const { data: pollingData } = usePollingItems(operatorId);
  const history = useNotificationHistory(
    operatorId,
    isOpen && activeTab === 'history',
  );
  const add = useAddNotificationRecipient();

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'client' | 'internal'>('client');
  const [addError, setAddError] = useState<string | null>(null);

  const recipients = data?.recipients ?? [];

  // Only offer events for how this operator actually syncs: a Transmormer
  // toggle when it has any transmormer flow, and a legacy section per legacy
  // integration it has polling/configs for. Integrations it doesn't have never
  // appear.
  const sections = useMemo<EventSection[]>(() => {
    const pollingItems = pollingData?.polling ?? [];
    const hasTransmormer = pollingItems.some((item) => item.useTransmormer);

    const legacyVendors = new Set<string>();
    for (const item of pollingItems) {
      if (item.useTransmormer) continue;
      for (const party of [item.origin, ...(item.destinations ?? [])]) {
        if (party !== HUB && LEGACY_INTEGRATION_EVENTS[party]) {
          legacyVendors.add(party);
        }
      }
    }

    return [
      { title: '', events: [INTEGRATION_DISABLED_EVENT] },
      ...(hasTransmormer
        ? [{ title: 'Transmormer notifications', events: [TRANSMORMER_EVENT] }]
        : []),
      ...[...legacyVendors].sort().map((vendor) => ({
        title: vendor,
        events: LEGACY_INTEGRATION_EVENTS[vendor],
      })),
    ];
  }, [pollingData]);

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) {
      setAddError('Email is required.');
      return;
    }
    setAddError(null);
    try {
      await add.mutateAsync({
        operatorId,
        data: {
          email,
          operatorName,
          recipientRole: newRole,
          notificationSettings: allEnabledDraft(sectionKeys(sections)),
        },
      });
      setNewEmail('');
      setNewRole('client');
    } catch (error) {
      setAddError(
        error instanceof Error ? error.message : 'Failed to add recipient',
      );
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="edit-drawer-overlay" onClick={onClose}></div>
      <div
        className="edit-drawer open notif-drawer"
        data-testid="notification-settings-drawer"
      >
        <div className="edit-drawer-header">
          <h2>Notifications{operatorName ? ` — ${operatorName}` : ''}</h2>
          <button className="edit-drawer-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M4 4L16 16M16 4L4 16"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="edit-drawer-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'recipients'}
            className={`edit-drawer-tab ${
              activeTab === 'recipients' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('recipients')}
          >
            Recipients
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'history'}
            className={`edit-drawer-tab ${
              activeTab === 'history' ? 'active' : ''
            }`}
            data-testid="notif-history-tab"
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {activeTab === 'history' && (
          <div className="edit-drawer-content" data-testid="notif-history">
            <ChangeHistoryEntries
              entries={history.data?.entries ?? []}
              isLoading={history.isLoading}
              error={history.error}
            />
          </div>
        )}

        {activeTab === 'recipients' && (
        <div className="edit-drawer-content">
          {canEdit && (
            <div className="notif-add-form">
              <h3 className="drawer-section-title">Add recipient</h3>
              <div className="notif-add-row">
                <input
                  type="email"
                  className="drawer-input"
                  placeholder="email@operator.com"
                  aria-label="New recipient email"
                  data-testid="notif-add-email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <select
                  className="notif-role-select"
                  aria-label="New recipient role"
                  value={newRole}
                  onChange={(e) =>
                    setNewRole(e.target.value as 'client' | 'internal')
                  }
                >
                  <option value="client">Client</option>
                  <option value="internal">Internal</option>
                </select>
                <button
                  className="btn-save"
                  data-testid="notif-add-submit"
                  onClick={handleAdd}
                  disabled={add.isPending}
                >
                  {add.isPending ? 'Adding...' : 'Add'}
                </button>
              </div>
              {addError && <span className="drawer-error">{addError}</span>}
            </div>
          )}

          {isLoading ? (
            <p className="drawer-empty">Loading recipients…</p>
          ) : recipients.length === 0 ? (
            <p className="drawer-empty" data-testid="notif-empty">
              No recipients configured for this operator.
            </p>
          ) : (
            recipients.map((recipient) => (
              <RecipientCard
                key={recipient.id}
                operatorId={operatorId}
                recipient={recipient}
                sections={sections}
                canEdit={canEdit}
              />
            ))
          )}
        </div>
        )}
      </div>
    </>
  );
}
